/**
 * Copyright 2025 John Huikku Alienrobot LLC
 * www.ALIENROBOT.com
 */

import { GitBranch, FolderGit2, RefreshCw, X } from 'lucide-react'

interface GitHubDuplicateModalProps {
  existingProjectName: string
  repoFullName: string
  onOpenExisting: () => void
  onUpdate: () => void
  onCloneNew: () => void
  onCancel: () => void
}

export function GitHubDuplicateModal({
  existingProjectName,
  repoFullName,
  onOpenExisting,
  onUpdate,
  onCloneNew,
  onCancel,
}: GitHubDuplicateModalProps) {
  return (
    <div 
      className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4"
      onClick={onCancel}
    >
      <div 
        className="bg-background-secondary border border-border rounded-lg w-full max-w-2xl shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-6 border-b border-border">
          <div className="flex items-start justify-between">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-accent/10 rounded-lg">
                <FolderGit2 className="w-6 h-6 text-accent" />
              </div>
              <div>
                <h2 className="text-xl font-title font-semibold text-foreground">
                  Repository Already Exists
                </h2>
                <p className="text-sm text-foreground-muted mt-1">
                  This repository is already cloned in your workspace
                </p>
              </div>
            </div>
            <button
              onClick={onCancel}
              className="p-1 hover:bg-background-hover rounded transition-colors"
              title="Close"
            >
              <X className="w-5 h-5 text-foreground-muted" />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-4">
          {/* Repository Info */}
          <div className="bg-background-tertiary border border-border rounded-lg p-4">
            <div className="flex items-center gap-2 text-sm">
              <GitBranch className="w-4 h-4 text-foreground-muted" />
              <span className="text-foreground-muted">Repository:</span>
              <code className="text-accent font-mono">{repoFullName}</code>
            </div>
            <div className="flex items-center gap-2 text-sm mt-2">
              <FolderGit2 className="w-4 h-4 text-foreground-muted" />
              <span className="text-foreground-muted">Existing Project:</span>
              <span className="text-foreground font-semibold">{existingProjectName}</span>
            </div>
          </div>

          {/* Options */}
          <div className="space-y-3">
            <p className="text-sm text-foreground-muted">
              What would you like to do?
            </p>

            {/* Option 1: Open Existing */}
            <button
              onClick={onOpenExisting}
              className="w-full p-4 bg-background-tertiary hover:bg-background-hover border border-border hover:border-accent/50 rounded-lg transition-all text-left group"
            >
              <div className="flex items-start gap-3">
                <div className="p-2 bg-blue-500/10 rounded-lg group-hover:bg-blue-500/20 transition-colors">
                  <FolderGit2 className="w-5 h-5 text-blue-400" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-foreground group-hover:text-accent transition-colors">
                    Open Existing Project
                  </h3>
                  <p className="text-xs text-foreground-muted mt-1">
                    Switch to the existing project without downloading anything
                  </p>
                </div>
              </div>
            </button>

            {/* Option 2: Update */}
            <button
              onClick={onUpdate}
              className="w-full p-4 bg-background-tertiary hover:bg-background-hover border border-border hover:border-accent/50 rounded-lg transition-all text-left group"
            >
              <div className="flex items-start gap-3">
                <div className="p-2 bg-green-500/10 rounded-lg group-hover:bg-green-500/20 transition-colors">
                  <RefreshCw className="w-5 h-5 text-green-400" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-foreground group-hover:text-accent transition-colors">
                    Update Existing Project
                  </h3>
                  <p className="text-xs text-foreground-muted mt-1">
                    Pull the latest changes from GitHub and open the project
                  </p>
                </div>
              </div>
            </button>

            {/* Option 3: Clone New */}
            <button
              onClick={onCloneNew}
              className="w-full p-4 bg-background-tertiary hover:bg-background-hover border border-border hover:border-accent/50 rounded-lg transition-all text-left group"
            >
              <div className="flex items-start gap-3">
                <div className="p-2 bg-purple-500/10 rounded-lg group-hover:bg-purple-500/20 transition-colors">
                  <GitBranch className="w-5 h-5 text-purple-400" />
                </div>
                <div className="flex-1">
                  <h3 className="font-semibold text-foreground group-hover:text-accent transition-colors">
                    Clone as New Project
                  </h3>
                  <p className="text-xs text-foreground-muted mt-1">
                    Create a separate copy (useful for different branches or experiments)
                  </p>
                </div>
              </div>
            </button>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-border bg-background-tertiary/50">
          <p className="text-xs text-foreground-muted text-center">
            💡 Tip: You can always update projects later using the sync button in the header
          </p>
        </div>
      </div>
    </div>
  )
}

